/**
 * Account Access Guard Component
 * Comprehensive access control based on account status and subscription
 * Integrates with Stripe billing and company management
 */

import React, { useState, useEffect, useCallback, ReactNode } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton
} from '@mui/material'
import {
  Lock as LockIcon,
  Payment as PaymentIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon
} from '@mui/icons-material'
import Parse from 'parse'
import { accountStatusService, AccountStatus, ActionPermissionResult } from '../../services/AccountStatusService'

export interface AccountAccessGuardProps {
  children: ReactNode
  action: 'create_room' | 'join_room' | 'join_custom_room' | 'access_dashboard' | 'access_company_management' | 'access_billing' | 'access_profile'
  resource?: string
  onProceed?: () => void
  onCancel?: () => void
  showDialog?: boolean
  fallbackComponent?: ReactNode
  loadingComponent?: ReactNode
  isGlobalAdmin?: boolean // Bypass cloud function calls for Global Admins
  authReady?: boolean // Defer checks until Parse session/token is ready
}

interface GuardState {
  loading: boolean
  permissionResult: ActionPermissionResult | null
  accountStatus: AccountStatus | null
  showDialog: boolean
  error: string | null
}

export const AccountAccessGuard: React.FC<AccountAccessGuardProps> = ({
  children,
  action,
  resource,
  onProceed,
  onCancel,
  showDialog: controlledShowDialog,
  fallbackComponent,
  loadingComponent,
  isGlobalAdmin = false,
  authReady = true
}) => {
  const [state, setState] = useState<GuardState>({
    loading: true,
    permissionResult: null,
    accountStatus: null,
    showDialog: false,
    error: null
  })

  const checkAccess = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }))
    
    try {
      // Debug logging for Global Admin status
      console.log(`[AccountAccessGuard] Checking access for action: ${action}, isGlobalAdmin: ${isGlobalAdmin}`)
      
      // Global Admins bypass all permission checks
      if (isGlobalAdmin) {
        console.log('[AccountAccessGuard] Global admin detected, bypassing permission check')
        const globalAdminResult: ActionPermissionResult = {
          allowed: true,
          reason: 'Global admin access',
          accountStatus: {
            subscriptionStatus: 'active',
            paymentStatus: 'paid',
            restrictions: [],
            company: null,
            userRole: 'admin'
          }
        }
        
        const globalAdminAccountStatus: AccountStatus = {
          isGlobalAdmin: true,
          hasFullAccess: true,
          subscriptionStatus: 'active',
          paymentStatus: 'paid',
          canCreateRooms: true,
          canJoinRooms: true,
          canAccessDashboard: true,
          canAccessCompanyManagement: true,
          canAccessAllFeatures: true,
          restrictions: [],
          company: null,
          userRole: 'admin'
        }
        
        setState(prev => ({
          ...prev,
          loading: false,
          permissionResult: globalAdminResult,
          accountStatus: globalAdminAccountStatus,
          showDialog: false
        }))
        
        return true
      }

      // Check if user is authenticated before making cloud function calls
      const currentUser = Parse.User.current()
      if (!currentUser) {
        console.warn('[AccountAccessGuard] No authenticated user, skipping permission check')
        setState(prev => ({
          ...prev,
          loading: false,
          error: 'User not authenticated',
          showDialog: false
        }))
        return false
      }

      // The cloud checkActionPermission only re-runs getAccountStatus, so a fresh cached status
      // that allows the action saves that call. A deny is never final from the cache: the user
      // may have just upgraded (the guard sends them to /billing), so the cloud decides.
      const cachedDecision = accountStatusService.getFreshCachedActionPermission(action, resource)
      const permissionResult =
        cachedDecision?.allowed === true
          ? cachedDecision
          : await accountStatusService.checkActionPermission(action, resource)
      
      // Get account status for additional context
      const accountStatus = await accountStatusService.getAccountStatus()
      
      setState(prev => ({
        ...prev,
        loading: false,
        permissionResult,
        accountStatus,
        showDialog: !permissionResult.allowed && (controlledShowDialog !== false)
      }))
      
      return permissionResult.allowed
    } catch (error) {
      console.error('Error checking access:', error)
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to check permissions',
        showDialog: true
      }))
      return false
    }
  }, [action, resource, isGlobalAdmin])

  const handleAction = async () => {
    const hasAccess = await checkAccess()
    if (hasAccess && onProceed) {
      onProceed()
    }
  }

  const handleCloseDialog = () => {
    setState(prev => ({ ...prev, showDialog: false }))
    if (onCancel) {
      onCancel()
    }
  }

  const handleContactSupport = () => {
    // Navigate to support or billing
    window.open('/billing', '_blank')
    handleCloseDialog()
  }

  const handleUpgrade = () => {
    // Navigate to billing/upgrade
    window.open('/billing', '_blank')
    handleCloseDialog()
  }

  const getActionText = () => {
    switch (action) {
      case 'create_room': return 'Create Room'
      case 'join_room': return 'Join Room'
      case 'join_custom_room': return 'Join Custom Room'
      case 'access_dashboard': return 'Access Dashboard'
      case 'access_company_management': return 'Access Company Management'
      case 'access_billing': return 'Access Billing'
      case 'access_profile': return 'Access Profile'
      default: return 'Perform Action'
    }
  }

  const getRestrictionTitle = () => {
    return `${getActionText()} Restricted`
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircleIcon color="success" />
      case 'trialing': return <InfoIcon color="info" />
      case 'past_due': return <WarningIcon color="warning" />
      case 'canceled':
      case 'cancelled': return <CancelIcon color="error" />
      case 'none': return <LockIcon color="disabled" />
      default: return <InfoIcon color="disabled" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success'
      case 'trialing': return 'info'
      case 'past_due': return 'warning'
      case 'canceled':
      case 'cancelled': return 'error'
      case 'none': return 'default'
      default: return 'default'
    }
  }

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'success'
      case 'overdue': return 'error'
      case 'failed': return 'error'
      case 'none': return 'default'
      default: return 'default'
    }
  }

  // Track isGlobalAdmin changes
  useEffect(() => {
    console.log(`[AccountAccessGuard] isGlobalAdmin prop changed to: ${isGlobalAdmin}`)
  }, [isGlobalAdmin])

  // Check access on mount with a small delay to ensure user session is established
  useEffect(() => {
    if (!authReady) {
      return
    }
    const timer = setTimeout(() => {
      checkAccess()
    }, 100) // Small delay to ensure Parse session is established
    
    return () => clearTimeout(timer)
  }, [action, resource, isGlobalAdmin, authReady, checkAccess]) // Added isGlobalAdmin/authReady and checkAccess to dependencies

  // Show loading state
  if (state.loading) {
    if (loadingComponent) {
      return <>{loadingComponent}</>
    }
    
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={2}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ ml: 1 }}>
          Checking permissions...
        </Typography>
      </Box>
    )
  }

  // Show error state
  if (state.error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <Typography variant="body2">
          Error checking permissions: {state.error}
        </Typography>
        <Button size="small" onClick={checkAccess} sx={{ mt: 1 }}>
          Retry
        </Button>
      </Alert>
    )
  }

  // Show fallback component if access denied and fallback provided
  if (!state.permissionResult?.allowed && fallbackComponent) {
    return <>{fallbackComponent}</>
  }

  // Show children if access allowed
  if (state.permissionResult?.allowed) {
    return <>{children}</>
  }

  // Show access denied dialog
  return (
    <>
      <div onClick={handleAction}>
        {children}
      </div>
      
      <Dialog
        open={state.showDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <LockIcon color="error" sx={{ mr: 1 }} />
              {getRestrictionTitle()}
            </Box>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                {state.permissionResult?.reason || 'Access restricted'}
              </Typography>
            </Alert>
            
            {state.accountStatus && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Account Status
                </Typography>
                
                <Box display="flex" gap={1} sx={{ mb: 2 }}>
                  <Chip
                    icon={getStatusIcon(state.accountStatus.subscriptionStatus)}
                    label={`Subscription: ${state.accountStatus.subscriptionStatus}`}
                    color={getStatusColor(state.accountStatus.subscriptionStatus) as any}
                    variant="outlined"
                  />
                  <Chip
                    icon={<PaymentIcon />}
                    label={`Payment: ${state.accountStatus.paymentStatus}`}
                    color={getPaymentStatusColor(state.accountStatus.paymentStatus) as any}
                    variant="outlined"
                  />
                </Box>
                
                {state.accountStatus.company && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Company Information
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <BusinessIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={state.accountStatus.company.name}
                          secondary={`ID: ${state.accountStatus.company.id}`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <PersonIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={`Role: ${state.accountStatus.userRole || 'User'}`}
                          secondary={`Status: ${state.accountStatus.company.isActive ? 'Active' : 'Inactive'}`}
                        />
                      </ListItem>
                    </List>
                  </Box>
                )}
                
                {state.accountStatus.restrictions.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Restrictions
                    </Typography>
                    <List dense>
                      {state.accountStatus.restrictions.map((restriction, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <WarningIcon color="warning" />
                          </ListItemIcon>
                          <ListItemText primary={restriction} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Cancel
          </Button>
          
          {state.accountStatus?.subscriptionStatus === 'none' && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpgrade}
              startIcon={<PaymentIcon />}
            >
              Subscribe Now
            </Button>
          )}
          
          {state.accountStatus?.paymentStatus === 'overdue' && (
            <Button
              variant="contained"
              color="warning"
              onClick={handleContactSupport}
              startIcon={<PaymentIcon />}
            >
              Update Payment
            </Button>
          )}
          
          {state.accountStatus?.subscriptionStatus === 'canceled' && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpgrade}
              startIcon={<PaymentIcon />}
            >
              Renew Subscription
            </Button>
          )}
          
          <Button
            variant="outlined"
            onClick={handleContactSupport}
            startIcon={<InfoIcon />}
          >
            Contact Support
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

// Convenience components for specific actions
export const RoomCreationGuard: React.FC<Omit<AccountAccessGuardProps, 'action'>> = (props) => (
  <AccountAccessGuard {...props} action="create_room" />
)

export const RoomJoiningGuard: React.FC<Omit<AccountAccessGuardProps, 'action'>> = (props) => (
  <AccountAccessGuard {...props} action="join_room" />
)

export const CustomRoomJoiningGuard: React.FC<Omit<AccountAccessGuardProps, 'action'>> = (props) => (
  <AccountAccessGuard {...props} action="join_custom_room" />
)

export const DashboardAccessGuard: React.FC<Omit<AccountAccessGuardProps, 'action'>> = (props) => (
  <AccountAccessGuard {...props} action="access_dashboard" />
)

export const CompanyManagementGuard: React.FC<Omit<AccountAccessGuardProps, 'action'>> = (props) => (
  <AccountAccessGuard {...props} action="access_company_management" />
)

export const BillingAccessGuard: React.FC<Omit<AccountAccessGuardProps, 'action'>> = (props) => (
  <AccountAccessGuard {...props} action="access_billing" />
)

export const ProfileAccessGuard: React.FC<Omit<AccountAccessGuardProps, 'action'>> = (props) => (
  <AccountAccessGuard {...props} action="access_profile" />
)
